import classNames from 'classnames'
import React, { useState } from 'react'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { AccordionButton } from '../Accordion/AccordionButton'
import { Collapse } from '../Collapse'
import { MarketSection } from '../MarketSection'
import { Paragraph } from '../Paragraph'
import { SupText } from '../SupText'

export interface KeyTermsProps {
  title: string
  text: string
}

export interface KeyTermsBlockProps {
  id?: string
  title: string
  text?: string
  terms?: KeyTermsProps[]
}

export const KeyTermsBlock = ({ id, text, title, terms }: KeyTermsBlockProps) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <MarketSection id={id}>
      <div
        className={classNames(
          'flex flex-col gap-30 overflow-hidden',
          'border border-neutral-lighter rounded-15',
          'col-span-full',
          'sm:col-start-2 sm:col-span-4',
          'md:col-start-1 md:col-span-full',
          'lg:col-start-2 lg:col-span-6 lg:-mx-75',
          'xl:col-start-2 xl:col-span-8 xl:-mx-45',
          'xxl:col-start-4'
        )}
      >
        <div className={classNames('flex flex-col my-30 mx-30 lg:mx-60 lg:my-60')}>
          <div
            className={classNames('flex flex-col')}
            onClick={() => setExpanded(current => !current)}
          >
            <div className={classNames('flex flex-row items-center')}>
              {!isNullOrWhiteSpace(title) && (
                <h2 className="font-h3 text-h3 text-secondary-darker flex-1">{title}</h2>
              )}
              <AccordionButton
                additionalClassName="lg:hidden"
                iconColor="text-secondary-darker"
                active={expanded}
              />
            </div>
          </div>
          <KeyTermsTable terms={terms ?? []} />
          <Collapse
            className="transition-all duration-200"
            contentClassName="flex flex-col pt-20 tb:justify-end lg:hidden "
            isOpen={expanded}
          >
            <KeyTermsAccordion terms={terms ?? []} />
            {!isNullOrWhiteSpace(text) && (
              <Paragraph className="font-body mt-20 text-t-200 sm:text-body text-secondary-darker">
                {text}
              </Paragraph>
            )}
          </Collapse>
          {!isNullOrWhiteSpace(text) && (
            <Paragraph className="font-body text-body mt-30 hidden lg:block lg:text-t-400 text-secondary-darker">
              {text}
            </Paragraph>
          )}
        </div>
      </div>
    </MarketSection>
  )
}

const KeyTermsTable = (props: { terms: KeyTermsProps[] }) => (
  <table className={classNames('hidden lg:table mt-30 table-auto')}>
    {!!props.terms && (
      <tbody className={classNames('w-full')}>
        {props.terms.map((row, trIndex) => (
          <tr
            key={trIndex}
            className={classNames('border-solid border-b border-neutral-lighter ')}
          >
            <td className={classNames('py-20 px-20 whitespace-nowrap flex items-start')}>
              <SupText as={'h3'} className="text-body text-400 text-secondary-darker">
                {row.title}
              </SupText>
            </td>
            <td className={classNames('py-20 px-20 ')}>
              <Paragraph className="text-body font-body text-secondary-darker">
                {row.text}
              </Paragraph>
            </td>
          </tr>
        ))}
      </tbody>
    )}
  </table>
)

const KeyTermsAccordion = (props: { terms: KeyTermsProps[] }) => (
  <div className={classNames('flex w-full flex-col lg:hidden')}>
    {!!props.terms && (
      <ul>
        {props.terms.map((row, rowIndex) => (
          <li
            key={rowIndex}
            className={classNames(
              'py-20 border-solid border-b border-neutral-lighter gap-15 flex flex-col'
            )}
          >
            <div key={`${rowIndex}`} className={classNames('gap-5 flex flex-col')}>
              <SupText
                as={'h3'}
                className="text-400 text-t-300 sm:text-t-500 text-secondary-darker"
              >
                {row.title}
              </SupText>
              <Paragraph className="font-body text-t-300 sm:text-t-400 text-secondary-darker">
                {row.text}
              </Paragraph>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
)
